import React from 'react';
import { useAgentHeartBeatService } from '../services/agentHeartBeatService';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';

interface AgentStatusIndicatorProps {
  agentId?: string;
  isActive?: boolean;
  statusMessage?: string;
  isLoading?: boolean;
  size?: number;
  isMarkedForDeletion?: boolean;
  estimatedDeletionDateUtc?: string;
}

const AgentStatusIndicator: React.FC<AgentStatusIndicatorProps> = ({
  agentId,
  isActive,
  statusMessage,
  isLoading,
  size = 3,
  isMarkedForDeletion,
  estimatedDeletionDateUtc,
}) => {
  const { getAgentLastHeartbeat } = useAgentHeartBeatService();
  const { selectedTenantId } = useTenantSelection();

  const [heartbeatTitle, setHeartbeatTitle] = React.useState<string | null>(null);
  const [isLoadingHeartbeat, setIsLoadingHeartbeat] = React.useState(false);
  const hasFetchedRef = React.useRef(false);

  const dotSize = `${size * 4}px`;

  // ✅ Convert UTC → Local time
  const formatToLocalTime = (utcDate?: string) => {
    if (!utcDate) return 'N/A';
    try {
      const date = new Date(utcDate);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Invalid date';
    }
  };

  // ✅ Relative time (UX improvement)
  const getRelativeTime = (utcDate?: string) => {
    if (!utcDate) return '';
    const diff = Date.now() - new Date(utcDate).getTime();
    const mins = Math.floor(diff / 60000);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;

    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;

    const days = Math.floor(hrs / 24);
    return `${days} day(s) ago`;
  };

  // ✅ Fetch ONLY on hover + inactive (skip entirely for agents marked for deletion)
  const handleMouseEnter = async () => {
    if (isMarkedForDeletion || isActive || !agentId || hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    setIsLoadingHeartbeat(true);

    try {
      const response = await getAgentLastHeartbeat(selectedTenantId as string, agentId);

      if (response?.data) {
        const utc = response.data.lastHeartBeatUtc;

        setHeartbeatTitle(
          <>
            <span className="text-gray-400 text-[11px]">Last seen</span>
            <br />
            <span className="text-white font-medium">{formatToLocalTime(utc)}</span>
            <br />
            <span className="text-gray-500 text-[10px]">{getRelativeTime(utc)}</span>
          </>,
        );
      } else {
        setHeartbeatTitle('No heartbeat data available');
      }
    } catch {
      setHeartbeatTitle('Error fetching heartbeat info');
    } finally {
      setIsLoadingHeartbeat(false);
    }
  };

  return (
    <div className="flex justify-center">
      <div className="relative group flex items-center justify-center" onMouseEnter={handleMouseEnter}>
        {/* Keyframes */}
        <style>{`
        @keyframes heartbeatPulse {
          0% {
            transform: scale(1);
            opacity: 0.9;
          }
          50% {
            transform: scale(1.8);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 0;
          }
        }

        @keyframes loadingBlink {
          0% {
            background-color: #ef4444;
          }
          50% {
            background-color: #f59e0b;
          }
          100% {
            background-color: #22c55e;
          }
        }

        @keyframes floating {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-2px);
          }
          100% {
            transform: translateY(0px);
          }
        }
      `}</style>

        {/* Glow Pulse Ring */}
        {!isLoading && (
          <span
            className={`
            absolute rounded-full
            ${isMarkedForDeletion ? 'bg-orange-400/40' : isActive ? 'bg-green-400/40' : 'bg-red-400/40'}
          `}
            style={{
              width: dotSize,
              height: dotSize,
              animation: 'heartbeatPulse 2s ease-out infinite',
            }}
          />
        )}

        {/* Main Status Dot */}
        <div
          className={`
          relative rounded-full
          transition-all duration-300
          group-hover:scale-125
          shadow-lg
          ${
            isLoading
              ? ''
              : isMarkedForDeletion
                ? 'bg-orange-500 shadow-orange-500/60'
                : isActive
                  ? 'bg-green-500 shadow-green-500/60'
                  : 'bg-red-500 shadow-red-500/60'
          }
        `}
          style={{
            width: dotSize,
            height: dotSize,
            animation: isLoading ? 'loadingBlink 1.4s ease-in-out infinite' : 'floating 3s ease-in-out infinite',
          }}
        />

        {/* Tooltip */}
        <div
          className="
          pointer-events-none
          absolute bottom-full left-1/2
          z-50 mb-4
          flex -translate-x-1/2 translate-y-2
          scale-95 opacity-0
          transition-all duration-300 ease-out
          group-hover:translate-y-0
          group-hover:scale-100
          group-hover:opacity-100
        "
        >
          <div
            className="
            relative overflow-hidden
            rounded-2xl
            border border-white/10
            bg-black/85
            backdrop-blur-xl
            shadow-[0_10px_40px_rgba(0,0,0,0.45)]
            px-4 py-3
            min-w-[220px]
            max-w-[320px]
          "
          >
            {/* Top shine */}
            <div
              className="
              absolute inset-0
              bg-gradient-to-br
              from-white/10
              via-transparent
              to-transparent
              pointer-events-none
            "
            />

            {/* Status Indicator Row */}
            <div className="relative z-10 flex items-start gap-3">
              {/* Tiny status icon */}
              <div
                className={`
                mt-1 h-2.5 w-2.5 rounded-full shrink-0
                ${isMarkedForDeletion ? 'bg-orange-400' : isLoading ? 'bg-yellow-400' : isActive ? 'bg-green-400' : 'bg-red-400'}
              `}
              />

              <div className="flex flex-col">
                <span className="text-white text-xs font-semibold">
                  {isMarkedForDeletion
                    ? 'Agent marked for deletion'
                    : isLoadingHeartbeat
                      ? 'Loading agent info...'
                      : isActive
                        ? 'Agent is active'
                        : 'Agent is inactive'}
                </span>

                {isMarkedForDeletion ? (
                  <div className="mt-1 text-xs text-gray-300 leading-relaxed">
                    <span className="text-gray-400 text-[11px]">Estimated deletion</span>
                    <br />
                    <span className="text-white font-medium">{formatToLocalTime(estimatedDeletionDateUtc)}</span>
                  </div>
                ) : (
                  !isActive && heartbeatTitle && <div className="mt-1 text-xs text-gray-300 leading-relaxed">{heartbeatTitle}</div>
                )}
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div
            className="
            absolute left-1/2 top-full
            h-3 w-3
            -translate-x-1/2 -translate-y-1/2
            rotate-45
            border-r border-b border-white/10
            bg-black/85
            backdrop-blur-xl
          "
          />
        </div>
      </div>
    </div>
  );
};

export default AgentStatusIndicator;
